// Components
import { Skeleton, Stack } from "@mantine/core";
import { FormField } from "@benchmark/ui/components/atomic";
import { CardSelect } from "@benchmark/ui/components/atomic";
import { EnhancedFormActions } from "@benchmark/ui/components/atomic";

// Hooks
import { useForm } from "@mantine/form";
import { useCallback } from "react";
import { useMedplum, useMedplumProfile } from "@medplum/react";
import { useNavigate } from "react-router";
import { useFormSubmission } from "@benchmark/ui/hooks";
import { useFormProtection } from "@benchmark/ui/hooks";
import { useStableParams } from "@/hooks/common/useStableParams";
import { usePatientResource } from "@/hooks/shared/useFHIRResource";

// Types
import type { Flag, Patient, Practitioner } from "@medplum/fhirtypes";
import type { FlagFormData } from "@benchmark/core/types";

// Utils
import {
  showErrorNotification,
  showSuccessNotification,
} from "@benchmark/ui/utils";
import { createFlagFacade } from "@/facades/flag.facade";
import {
  FLAG_CODE_OPTIONS,
  FLAG_CODE_CARD_OPTIONS,
  FLAG_LOCAL_CODE_SYSTEM,
  getFlagCategoryByCode,
} from "@benchmark/core/constants";

/**
 * Props for the FlagForm component.
 * @agentic connections
 */
export type FlagFormProps = {
  patientId?: string;
  flag?: Flag;
  onSuccess?: (flagId: string) => void;
  onCancel?: () => void;
};

/** Local operational flag options for the form (excludes patient-paused and cocm-enrolled). */
const LOCAL_FLAG_CARD_OPTIONS = FLAG_CODE_CARD_OPTIONS.filter((o) => {
  const source = FLAG_CODE_OPTIONS.find((f) => f.code === o.value);
  return (
    source?.system === FLAG_LOCAL_CODE_SYSTEM &&
    o.value !== "patient-paused" &&
    o.value !== "cocm-enrolled"
  );
});

/**
 * Form for creating patient flags (local operational flags only).
 * @agentic connections
 */
export const FlagForm = ({
  patientId,
  onSuccess,
  onCancel,
}: FlagFormProps = {}) => {
  const medplum = useMedplum();
  const flagFacade = createFlagFacade(medplum);
  const navigate = useNavigate();
  const { patientId: urlPatientId } = useStableParams();
  const effectivePatientId = patientId || urlPatientId;
  const { data: patientResource } = usePatientResource(effectivePatientId);
  const patient = patientResource as Patient | undefined;
  const profile = useMedplumProfile() as Practitioner;

  const form = useForm<FlagFormData>({
    initialValues: {
      status: "active",
      category: "",
      code: "",
    },
    validate: {
      code: (value) => (!value ? "Flag is required" : null),
    },
  });

  const { markAsClean, ConfirmationModal } = useFormProtection(form, {
    enabled: true,
    blockNavigation: true,
    confirmationMessage:
      "You have unsaved flag changes. Are you sure you want to close?",
  });

  const { createSubmissionHandler, isSubmitting } = useFormSubmission();

  const handleSubmit = useCallback(
    async (formData: FlagFormData) => {
      if (!patient || !profile) {
        showErrorNotification("Missing patient or practitioner information.");
        return;
      }
      // Ensure category is set based on selected code if not explicitly chosen
      if (!formData.category && formData.code) {
        // Use centralized category resolution from constants
        const category = getFlagCategoryByCode(formData.code);
        if (category) {
          formData.category = category;
        }
      }
      try {
        const created = await flagFacade.upsert({
          formData,
          patient,
          practitioner: profile,
        });

        showSuccessNotification(`Flag created successfully.`);
        markAsClean();
        if (onSuccess && created.id) {
          onSuccess(created.id);
        } else {
          navigate(`/Patient/${patient.id}`)?.catch(console.error);
        }
      } catch (error) {
        showErrorNotification(error, {
          title: "Unable to Create Flag",
        });
      }
    },
    [patient, profile, flagFacade, navigate, onSuccess, markAsClean],
  );

  if (!patient || !profile) {
    return <Skeleton height={200} />;
  }

  return (
    <form
      onSubmit={form.onSubmit(
        createSubmissionHandler(handleSubmit),
        (validationErrors) => {
          console.error("validationErrors", validationErrors);
          showErrorNotification(
            "Please fix the form errors before submitting.",
          );
        },
      )}
    >
      <Stack gap="md">
        <FormField label="Flag" variant="section" showDivider={false} required>
          <CardSelect
            placeholder="Select a Flag"
            data={LOCAL_FLAG_CARD_OPTIONS}
            value={form.values.code || null}
            onChange={(value) => {
              const selected = Array.isArray(value) ? value[0] : value;
              if (!selected) {
                form.setValues({ ...form.values, code: "", category: "" });
                return;
              }
              const category = getFlagCategoryByCode(selected);
              form.setValues({
                ...form.values,
                code: selected,
                category: category || form.values.category,
              });
            }}
            columns={2}
            error={!!form.errors.code}
          />
        </FormField>

        <EnhancedFormActions
          isSubmitting={isSubmitting}
          resourceText="Flag"
          isEditing={false}
          showCancel={true}
          onCancel={onCancel}
        />
      </Stack>
      <ConfirmationModal />
    </form>
  );
};
